/**
 * DependencyContainer - Injection de dépendances
 *
 * Configure et fournit toutes les dépendances de l'application
 * selon les principes de Clean Architecture
 */

import { ITransactionRepository } from '../../application/ports/output/ITransactionRepository';
import { IAccountRepository } from '../../application/ports/output/IAccountRepository';
import { ICashRegisterRepository } from '../../application/ports/output/ICashRegisterRepository';
import { IBiometricService } from '../../application/ports/output/IBiometricService';
import { ICardReaderService } from '../../application/ports/output/ICardReaderService';
import { IAuditLogger } from '../../application/ports/output/IAuditLogger';
import { IDepositGateway } from '../../application/ports/output/IDepositGateway';
import { ICbsAccountLookup } from '../../application/ports/output/ICbsAccountLookup';
import { ITypePieceService } from '../../application/ports/output/ITypePieceService';

import { LocalStorageTransactionRepository } from '../repositories/LocalStorageTransactionRepository';
import { LocalStorageAccountRepository } from '../repositories/LocalStorageAccountRepository';
import { InMemoryCashRegisterRepository } from '../repositories/InMemoryCashRegisterRepository';
import { HttpAccountRepository } from '../repositories/HttpAccountRepository';
import { BiometricFingerprintAdapter } from '../adapters/BiometricFingerprintAdapter';
import { SecurgenCardReaderAdapter } from '../adapters/SecurgenCardReaderAdapter';
import { RealBiometricFingerprintAdapter } from '../adapters/RealBiometricFingerprintAdapter';
import { RealSecurgenCardReaderAdapter } from '../adapters/RealSecurgenCardReaderAdapter';
import { ConsoleAuditLogger } from '../adapters/ConsoleAuditLogger';
import { CardLifeDepositGateway } from '../adapters/CardLifeDepositGateway';
import { CardLifeWithdrawalGateway } from '../adapters/CardLifeWithdrawalGateway';
import { CardLifeCbsAccountLookup } from '../adapters/CardLifeCbsAccountLookup';
import { CardLifeTypePieceAdapter } from '../adapters/CardLifeTypePieceAdapter';

/**
 * Container de dépendances (singleton)
 */
export class DependencyContainer {
  private static instance: DependencyContainer;

  // Repositories
  private _transactionRepository: ITransactionRepository;
  private _accountRepository: IAccountRepository;
  private _cashRegisterRepository: ICashRegisterRepository;

  // Services
  private _biometricService: IBiometricService;
  private _cardReaderService: ICardReaderService;
  private _auditLogger: IAuditLogger;
  private _depositGateway: IDepositGateway | null;
  private _withdrawalGateway: CardLifeWithdrawalGateway | null;
  private _cbsAccountLookup: ICbsAccountLookup;
  private _typePieceService: ITypePieceService;

  private constructor() {
    const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
    const useRemoteAccounts = ENV.VITE_USE_REMOTE_ACCOUNTS === 'true';
    const useRemoteDeposit = ENV.VITE_USE_REMOTE_DEPOSIT === 'true';
    const useRealBiometric = ENV.VITE_USE_REAL_BIOMETRIC === 'true';
    const useRealCardReader = ENV.VITE_USE_REAL_CARD_READER === 'true';
    const useRemoteWithdrawal = ENV.VITE_USE_REMOTE_WITHDRAWAL === 'true';

    // Initialiser les repositories
    this._transactionRepository = new LocalStorageTransactionRepository();
    this._accountRepository = useRemoteAccounts ? new HttpAccountRepository() : new LocalStorageAccountRepository();
    this._cashRegisterRepository = new InMemoryCashRegisterRepository();

    // Initialiser les services matériels (biométrie et carte)
    this._biometricService = useRealBiometric
      ? new RealBiometricFingerprintAdapter()
      : new BiometricFingerprintAdapter();

    this._cardReaderService = useRealCardReader
      ? new RealSecurgenCardReaderAdapter()
      : new SecurgenCardReaderAdapter();

    this._auditLogger = new ConsoleAuditLogger();
    this._depositGateway = useRemoteDeposit ? new CardLifeDepositGateway() : null;
    this._withdrawalGateway = useRemoteWithdrawal ? new CardLifeWithdrawalGateway() : null;
    this._cbsAccountLookup = new CardLifeCbsAccountLookup();
    this._typePieceService = new CardLifeTypePieceAdapter();

    console.log('[DependencyContainer] Dépendances initialisées', {
      biometric: useRealBiometric ? 'Production' : 'Development',
      cardReader: useRealCardReader ? 'Production' : 'Development',
      accounts: useRemoteAccounts ? 'HTTP' : 'LocalStorage',
      deposit: useRemoteDeposit ? 'CardLife' : 'Development',
      withdrawal: useRemoteWithdrawal ? 'CardLife' : 'Development',
    });
  }

  /**
   * Récupère l'instance unique du container
   */
  static getInstance(): DependencyContainer {
    if (!DependencyContainer.instance) {
      DependencyContainer.instance = new DependencyContainer();
    }
    return DependencyContainer.instance;
  }

  /**
   * Réinitialise le container (pour tests)
   */
  static reset(): void {
    DependencyContainer.instance = new DependencyContainer();
  }

  // Getters pour les dépendances

  get transactionRepository(): ITransactionRepository {
    return this._transactionRepository;
  }

  get accountRepository(): IAccountRepository {
    return this._accountRepository;
  }

  get cashRegisterRepository(): ICashRegisterRepository {
    return this._cashRegisterRepository;
  }

  get biometricService(): IBiometricService {
    return this._biometricService;
  }

  get cardReaderService(): ICardReaderService {
    return this._cardReaderService;
  }

  get auditLogger(): IAuditLogger {
    return this._auditLogger;
  }

  get depositGateway(): IDepositGateway | null {
    return this._depositGateway;
  }

  get withdrawalGateway(): CardLifeWithdrawalGateway | null {
    return this._withdrawalGateway;
  }

  get cbsAccountLookup(): ICbsAccountLookup {
    return this._cbsAccountLookup;
  }

  get typePieceService(): ITypePieceService {
    return this._typePieceService;
  }
}
